import { useState } from "react";
import { toast } from "sonner";
import { apiError, updateAdminUser, adminAdjustCredits, deleteAdminUser } from "@/lib/api";
import { Ban, Coins, Pencil, Trash2, ShieldCheck, Check, X } from "lucide-react";

const iconBtn =
    "p-2 border border-white/15 text-white/50 hover:text-white hover:border-white/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed";

const inputCls =
    "bg-obsidian border border-white/15 px-3 py-2 font-mono text-xs text-white outline-none focus:border-electric";

const CreditsForm = ({ userId, busy, onApply, onClose }) => {
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");

    const apply = () => {
        const amt = parseInt(amount, 10);
        if (!amt) {
            toast.error("Enter a non-zero amount (negative to deduct)");
            return;
        }
        onApply(amt, note);
    };

    return (
        <div className="px-5 pb-4 flex gap-2 flex-wrap items-center" data-testid={`credits-form-${userId}`}>
            <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="+500 / -200"
                className={`w-32 ${inputCls}`}
                data-testid={`credits-amount-${userId}`}
            />
            <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional)"
                className={`flex-1 min-w-[140px] ${inputCls}`}
                data-testid={`credits-note-${userId}`}
            />
            <button onClick={apply} disabled={busy} className={iconBtn} data-testid={`credits-apply-${userId}`}>
                <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClose} className={iconBtn}>
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
};

const EditNameForm = ({ userId, initialName, busy, onSave, onClose }) => {
    const [name, setName] = useState(initialName);

    const save = () => {
        if (!name.trim()) {
            toast.error("Name cannot be empty");
            return;
        }
        onSave(name.trim());
    };

    return (
        <div className="px-5 pb-4 flex gap-2 items-center" data-testid={`edit-form-${userId}`}>
            <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`flex-1 max-w-xs ${inputCls}`}
                data-testid={`edit-name-${userId}`}
            />
            <button onClick={save} disabled={busy} className={iconBtn} data-testid={`edit-save-${userId}`}>
                <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClose} className={iconBtn}>
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
};

const ActionButton = ({ onClick, disabled = false, active = false, activeCls = "border-blaze text-blaze", title, testId, Icon }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`${iconBtn} ${active ? activeCls : ""}`}
        title={title}
        data-testid={testId}
    >
        <Icon className="w-3.5 h-3.5" />
    </button>
);

const RowActions = ({ u, mode, busy, confirmDelete, isAdmin, setMode, onBlock, onDelete }) => (
    <div className="col-span-2 flex justify-end gap-1.5">
        <ActionButton
            onClick={() => setMode(mode === "credits" ? null : "credits")}
            active={mode === "credits"}
            title="Adjust credits"
            testId={`credits-button-${u.user_id}`}
            Icon={Coins}
        />
        <ActionButton
            onClick={() => setMode(mode === "edit" ? null : "edit")}
            active={mode === "edit"}
            title="Edit name"
            testId={`edit-button-${u.user_id}`}
            Icon={Pencil}
        />
        <ActionButton
            onClick={onBlock}
            disabled={busy || isAdmin}
            active={u.blocked}
            title={u.blocked ? "Unblock user" : "Block user"}
            testId={`block-button-${u.user_id}`}
            Icon={Ban}
        />
        <ActionButton
            onClick={onDelete}
            disabled={busy || isAdmin}
            active={confirmDelete}
            activeCls="border-blaze text-blaze bg-blaze/10"
            title={confirmDelete ? "Click again to confirm delete" : "Delete user"}
            testId={`delete-button-${u.user_id}`}
            Icon={Trash2}
        />
    </div>
);

const UserSummaryCells = ({ u, isAdmin }) => (
    <>
        <div className="col-span-4 pr-2 min-w-0">
            <div className="flex items-center gap-2 text-white/90 truncate">
                {isAdmin && <ShieldCheck className="w-3.5 h-3.5 text-blaze shrink-0" />}
                <span className="truncate">{u.display_name}</span>
                {u.blocked && (
                    <span
                        className="text-[9px] uppercase tracking-[0.2em] text-blaze border border-blaze/40 px-1.5 py-0.5 shrink-0"
                        data-testid={`blocked-badge-${u.user_id}`}
                    >
                        Blocked
                    </span>
                )}
            </div>
            <div className="text-white/40 truncate mt-0.5">{u.email}</div>
        </div>
        <div className="col-span-2 text-right font-bold text-white" data-testid={`user-credits-${u.user_id}`}>
            {u.credits.toLocaleString()}
        </div>
        <div className="col-span-2 text-right text-white/50">{u.credits_used.toLocaleString()}</div>
        <div className="col-span-2 text-right text-white/40">
            {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
        </div>
    </>
);

const DELETE_CONFIRM_RESET_MS = 4000;

const useUserRowActions = (u, onChanged) => {
    const [mode, setMode] = useState(null); // null | credits | edit
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    const run = async (fn, okMsg) => {
        setBusy(true);
        try {
            await fn();
            toast.success(okMsg);
            setMode(null);
            setConfirmDelete(false);
            onChanged();
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    const toggleBlock = () =>
        run(
            () => updateAdminUser(u.user_id, { blocked: !u.blocked }),
            u.blocked ? `${u.email} unblocked` : `${u.email} blocked`
        );

    const grantCredits = (amt, note) =>
        run(() => adminAdjustCredits(u.user_id, amt, note), `${amt > 0 ? "+" : ""}${amt} credits → ${u.email}`);

    const saveName = (name) =>
        run(() => updateAdminUser(u.user_id, { display_name: name }), "Name updated");

    const handleDelete = () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            setTimeout(() => setConfirmDelete(false), DELETE_CONFIRM_RESET_MS);
            return;
        }
        run(() => deleteAdminUser(u.user_id), `${u.email} deleted`);
    };

    return { mode, setMode, confirmDelete, busy, toggleBlock, grantCredits, saveName, handleDelete };
};

export const AdminUserRow = ({ u, onChanged }) => {
    const isAdmin = u.role === "admin";
    const { mode, setMode, confirmDelete, busy, toggleBlock, grantCredits, saveName, handleDelete } =
        useUserRowActions(u, onChanged);

    return (
        <div className="border-b border-white/5" data-testid={`admin-user-row-${u.user_id}`}>
            <div className="grid grid-cols-12 px-5 py-4 font-mono text-xs items-center">
                <UserSummaryCells u={u} isAdmin={isAdmin} />
                <RowActions
                    u={u}
                    mode={mode}
                    busy={busy}
                    confirmDelete={confirmDelete}
                    isAdmin={isAdmin}
                    setMode={setMode}
                    onBlock={toggleBlock}
                    onDelete={handleDelete}
                />
            </div>

            {confirmDelete && (
                <div className="px-5 pb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-blaze" data-testid={`delete-confirm-hint-${u.user_id}`}>
                    Click the trash icon again to permanently delete this user and all their data
                </div>
            )}

            {mode === "credits" && (
                <CreditsForm userId={u.user_id} busy={busy} onApply={grantCredits} onClose={() => setMode(null)} />
            )}

            {mode === "edit" && (
                <EditNameForm
                    userId={u.user_id}
                    initialName={u.display_name}
                    busy={busy}
                    onSave={saveName}
                    onClose={() => setMode(null)}
                />
            )}
        </div>
    );
};
